import { useTranslation } from 'react-i18next';
import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X, RefreshCw } from 'lucide-react';

interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  actions?: ToastAction[];
  retryable?: boolean;
  onRetry?: () => void;
  persistent?: boolean;
  sound?: boolean;
}

export interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type?: Toast['type'], duration?: number, options?: {
    actions?: ToastAction[];
    retryable?: boolean;
    onRetry?: () => void;
    persistent?: boolean;
    sound?: boolean;
  }) => void;
  dismissToast: (id: number) => void;
  showErrorToast: (message: string, onRetry?: () => void) => void;
  showSuccessToast: (message: string, actions?: ToastAction[]) => void;
  clearAllToasts: () => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Notification sound (using a public asset for now, or a standard beep)
const NOTIFICATION_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');
  const [toasts, setToasts] = useState<Toast[]>([]);

  const playNotificationSound = useCallback(() => {
    // Ne pas jouer de son si l'utilisateur n'a pas encore interagi avec la page
    // (Politique d'Autoplay des navigateurs modernes)
    try {
      const audio = new Audio(NOTIFICATION_SOUND_URL);
      audio.volume = 0.4; // Un peu plus discret
      
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          // Ignorer silencieusement l'erreur NotAllowedError (autoplay bloqué)
          if (e.name !== 'NotAllowedError') {
            console.warn('[Toast] Audio play failed:', e);
          }
        });
      }
    } catch (e) {
      // Ignorer les erreurs d'initialisation
    }
  }, []);

  const showToast = useCallback((
    message: string,
    type: Toast['type'] = 'info',
    duration: number = 3500,
    options: {
      actions?: ToastAction[];
      retryable?: boolean;
      onRetry?: () => void;
      persistent?: boolean;
      sound?: boolean;
    } = {}
  ) => {
    const id = Date.now() + Math.random();
    const toast: Toast = {
      id,
      message,
      type,
      duration: options.persistent ? undefined : duration,
      actions: options.actions,
      retryable: options.retryable,
      onRetry: options.onRetry,
      persistent: options.persistent,
      sound: options.sound
    };

    setToasts((prev) => [...prev, toast]);

    if (options.sound) {
      playNotificationSound();
    }

    if (!options.persistent) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, [playNotificationSound]);

  const showErrorToast = useCallback((message: string, onRetry?: () => void) => {
    showToast(message, 'error', 5000, {
      retryable: !!onRetry,
      onRetry,
      actions: onRetry ? [{
        label: 'Réessayer',
        onClick: onRetry,
        variant: 'primary'
      }] : undefined
    });
  }, [showToast]);

  const showSuccessToast = useCallback((message: string, actions?: ToastAction[]) => {
    showToast(message, 'success', 3000, { actions });
  }, [showToast]);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  useEffect(() => {
    const handleSocketUiEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ type: string; message: string; id?: string }>;
      if (customEvent.detail) {
        const { type, message } = customEvent.detail;
        showToast(message, type as Toast['type'], 4000);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('socket:ui-notification', handleSocketUiEvent);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('socket:ui-notification', handleSocketUiEvent);
      }
    };
  }, [showToast]);

  const getIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5" />;
      case 'error': return <XCircle className="w-5 h-5" />;
      case 'warning': return <AlertCircle className="w-5 h-5" />;
      case 'info': return <Info className="w-5 h-5" />;
    }
  };

  const getToastStyles = (type: Toast['type']) => {
    switch (type) {
      case 'success': return 'bg-green-600 border-green-700';
      case 'error': return 'bg-red-600 border-red-700';
      case 'warning': return 'bg-yellow-600 border-yellow-700';
      case 'info': return 'bg-blue-600 border-blue-700';
    }
  };

  return (
    <ToastContext.Provider value={{
      toasts,
      showToast,
      dismissToast,
      showErrorToast,
      showSuccessToast,
      clearAllToasts
    }}>
      {children}

      {/* Enhanced Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              ${getToastStyles(toast.type)}
              border-l-4 shadow-xl rounded-r-lg p-4 text-white
              transform transition-all duration-300 ease-out
              animate-fade-in-right cursor-pointer
              hover:shadow-2xl hover-scale-102
            `}
            onClick={() => !toast.persistent && dismissToast(toast.id)}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {getIcon(toast.type)}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-5">{toast.message}</p>

                {/* Actions */}
                {toast.actions && toast.actions.length > 0 && (
                  <div className="mt-3 flex space-x-2">
                    {toast.actions.map((action, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick();
                          if (!toast.persistent) {
                            dismissToast(toast.id);
                          }
                        }}
                        className={`
                          px-3 py-1 text-xs font-medium rounded-md transition-colors
                          ${action.variant === 'primary'
                            ? 'bg-white/20 hover:bg-white/30 text-white'
                            : 'bg-black/20 hover:bg-black/30 text-white/90'
                          }
                        `}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Retry button for retryable errors */}
                {toast.retryable && toast.onRetry && (
                  <div className="mt-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.onRetry?.();
                        dismissToast(toast.id);
                      }}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Réessayer
                    </button>
                  </div>
                )}
              </div>

              {/* Dismiss button */}
              <div className="flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dismissToast(toast.id);
                  }}
                  className="p-1 rounded-md hover:bg-black/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Progress bar for timed toasts */}
            {toast.duration && !toast.persistent && (
              <div className="mt-3 bg-black/20 rounded-full h-1 overflow-hidden">
                <div
                  className="h-full bg-white/40 transition-all duration-75 ease-linear"
                  style={{
                    animation: `shrink ${toast.duration}ms linear forwards`
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Global styles for animations */}
      <style>{`
        @keyframes fade-in-right {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        .animate-fade-in-right {
          animation: fade-in-right 0.3s ease-out;
        }

        .hover-scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </ToastContext.Provider>
  );
};
